import { useCallback, useMemo } from 'react';
import { BlockedNumber } from '../types';
import { normalizeNumberForBlocking } from '../utils';
import { useBlockedNumbersByTenant } from './use-blocked-numbers-by-tenant';

/**
 * Answers "is this number blocked for this tenant?" from the shared per-tenant blocked-number cache.
 * Tenants whose list is still loading or failed to load are treated as having no blocked numbers for lookups;
 * a failure is exposed through isError.
 */
export const useBlockedNumberLookup = ({ tenantIds }: { tenantIds: string[] }) => {
  const { blockedNumbersByTenant, isLoading, isError } = useBlockedNumbersByTenant(tenantIds);

  const indexByTenant = useMemo(() => {
    const index = new Map<string, Map<string, BlockedNumber>>();
    Object.entries(blockedNumbersByTenant).forEach(([tenantId, blockedNumbers]) => {
      const byNumber = new Map<string, BlockedNumber>();
      blockedNumbers.forEach((blockedNumber) => {
        const normalized = normalizeNumberForBlocking(blockedNumber.phoneNumber?.nationalNumber);
        if (normalized) byNumber.set(normalized, blockedNumber);
      });
      index.set(tenantId, byNumber);
    });
    return index;
  }, [blockedNumbersByTenant]);

  const getBlockedNumber = useCallback(
    (tenantId?: string, phoneNumber?: string | number): BlockedNumber | undefined => {
      const normalized = normalizeNumberForBlocking(phoneNumber);
      if (!tenantId || !normalized) return undefined;
      return indexByTenant.get(tenantId)?.get(normalized);
    },
    [indexByTenant]
  );

  const isNumberBlocked = useCallback(
    (tenantId?: string, phoneNumber?: string | number) => !!getBlockedNumber(tenantId, phoneNumber),
    [getBlockedNumber]
  );

  return { isNumberBlocked, getBlockedNumber, isLoading, isError };
};
